// 前端日期时间处理工具类 - 支持时区转换
export class DateHelper {
  // 服务器默认时区（北京时间 UTC+8）
  private static SERVER_TIMEZONE = 'Asia/Shanghai';
  
  /**
   * 获取当前设备时区
   */
  static getLocalTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * 获取当前设备时区偏移（分钟）
   */
  static getTimezoneOffset(): number {
    return new Date().getTimezoneOffset();
  }

  /**
   * 获取时区名称显示
   */
  static getTimezoneDisplay(): string {
    const offset = -this.getTimezoneOffset() / 60;
    const sign = offset >= 0 ? '+' : '';
    return `UTC${sign}${offset}`;
  }

  /**
   * 将Date对象转换为YYYY-MM-DD格式的字符串（用于input[type="date"]）
   */
  static formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * 将YYYY-MM-DD格式的字符串转换为本地Date对象
   */
  static parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * 将服务器日期时间转换为本地时间
   * @param dateStr 日期字符串 YYYY-MM-DD
   * @param timeStr 时间字符串 HH:mm 或 HH:mm:ss
   * @param serverTimezone 服务器时区（默认北京时间）
   * @returns 本地时间的 Date 对象
   */
  static serverToLocal(dateStr: string, timeStr: string, serverTimezone?: string): Date {
    const tz = serverTimezone || this.SERVER_TIMEZONE;
    // 构建完整的日期时间字符串
    const dateTimeStr = `${dateStr}T${timeStr}`;
    
    // 创建一个表示服务器时区时间的 Date 对象
    // 使用 Intl.DateTimeFormat 来处理时区转换
    const serverDate = new Date(dateTimeStr);
    
    // 获取服务器时区的偏移量
    const serverFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // 解析服务器时间
    const parts = serverFormatter.formatToParts(serverDate);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '0';
    
    const year = parseInt(getPart('year'));
    const month = parseInt(getPart('month')) - 1;
    const day = parseInt(getPart('day'));
    const hour = parseInt(getPart('hour'));
    const minute = parseInt(getPart('minute'));
    const second = parseInt(getPart('second'));
    
    // 创建 UTC 时间，然后转换到本地
    const utcDate = new Date(Date.UTC(year, month, day, hour, minute, second));
    
    // 计算服务器时区与 UTC 的差异
    const serverOffset = this.getTimezoneOffsetForZone(tz, utcDate);
    
    // 调整到 UTC，然后 JavaScript 会自动转换到本地时区
    return new Date(utcDate.getTime() - serverOffset * 60 * 1000);
  }

  /**
   * 获取指定时区在特定时间的 UTC 偏移（分钟）
   */
  private static getTimezoneOffsetForZone(timezone: string, date: Date): number {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return (tzDate.getTime() - utcDate.getTime()) / 60000;
  }

  /**
   * 将本地时间转换为服务器时区时间
   * @param localDate 本地 Date 对象
   * @param serverTimezone 服务器时区（默认北京时间）
   * @returns { date: YYYY-MM-DD, time: HH:mm }
   */
  static localToServer(localDate: Date, serverTimezone?: string): { date: string; time: string } {
    const tz = serverTimezone || this.SERVER_TIMEZONE;
    
    const formatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(localDate);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '00';
    
    const date = `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
    const time = `${getPart('hour')}:${getPart('minute')}`;
    
    return { date, time };
  }

  /**
   * 格式化预约时间显示（自动转换时区）
   * @param dateStr 服务器日期 YYYY-MM-DD
   * @param startTime 开始时间 HH:mm
   * @param endTime 结束时间 HH:mm
   * @returns 本地时区的日期和时间显示
   */
  static formatBookingTime(dateStr: string, startTime: string, endTime?: string): {
    localDate: string;
    localStartTime: string;
    localEndTime?: string;
    localDateObj: Date;
    isCrossDay: boolean;
    timezoneNote?: string;
  } {
    const startLocal = this.serverToLocal(dateStr, startTime);
    const localDateStr = this.formatDateForInput(startLocal);
    const localStartTimeStr = startLocal.toLocaleTimeString('zh-CN', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });

    let localEndTimeStr: string | undefined;
    let isCrossDay = false;

    if (endTime) {
      const endLocal = this.serverToLocal(dateStr, endTime);
      localEndTimeStr = endLocal.toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      
      // 检查是否跨天（结束时间早于开始时间说明跨天了）
      isCrossDay = this.formatDateForInput(endLocal) !== localDateStr;
    }

    // 如果时区不同，显示时区提示
    const localTz = this.getLocalTimezone();
    const timezoneNote = localTz !== this.SERVER_TIMEZONE 
      ? `(${this.getTimezoneDisplay()})` 
      : undefined;

    return {
      localDate: localDateStr,
      localStartTime: localStartTimeStr,
      localEndTime: localEndTimeStr,
      localDateObj: startLocal,
      isCrossDay,
      timezoneNote
    };
  }

  /**
   * 格式化日期为中文显示（带时区转换）
   */
  static formatChineseDate(date: Date | string, serverTime?: string): string {
    let dateObj: Date;
    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/) && serverTime) {
        // 如果有服务器时间，进行时区转换
        dateObj = this.serverToLocal(date, serverTime);
      } else if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateObj = this.parseLocalDate(date);
      } else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }
    
    return dateObj.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * 格式化日期时间为完整显示
   */
  static formatFullDateTime(dateStr: string, timeStr: string): string {
    const localDate = this.serverToLocal(dateStr, timeStr);
    return localDate.toLocaleString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  /**
   * 检查日期是否为今天（带时区转换）
   */
  static isToday(date: Date | string, serverTime?: string): boolean {
    let dateObj: Date;
    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/) && serverTime) {
        dateObj = this.serverToLocal(date, serverTime);
      } else if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateObj = this.parseLocalDate(date);
      } else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }
    
    const today = new Date();
    return dateObj.getDate() === today.getDate() &&
           dateObj.getMonth() === today.getMonth() &&
           dateObj.getFullYear() === today.getFullYear();
  }

  /**
   * 检查预约是否为即将到来（带时区转换）
   */
  static isUpcoming(dateStr: string, startTime: string): boolean {
    const bookingStart = this.serverToLocal(dateStr, startTime);
    return bookingStart.getTime() > Date.now();
  }

  /**
   * 获取相对时间描述（如"2小时后"、"明天"）
   */
  static getRelativeTime(dateStr: string, timeStr: string): string {
    const targetDate = this.serverToLocal(dateStr, timeStr);
    const now = new Date();
    const diffMs = targetDate.getTime() - now.getTime();
    
    if (diffMs < 0) {
      return '已过期';
    }
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 60) {
      return `${diffMinutes}分钟后`;
    } else if (diffHours < 24) {
      return `${diffHours}小时后`;
    } else if (diffDays === 1) {
      return '明天';
    } else if (diffDays < 7) {
      return `${diffDays}天后`;
    } else {
      return this.formatChineseDate(targetDate);
    }
  }

  /**
   * 获取当前日期的YYYY-MM-DD格式字符串
   */
  static getTodayString(): string {
    return this.formatDateForInput(new Date());
  }

  /**
   * 比较两个日期字符串是否为同一天
   */
  static isSameDay(date1: string, date2: string): boolean {
    return this.formatDateForInput(this.parseLocalDate(date1)) === 
           this.formatDateForInput(this.parseLocalDate(date2));
  }

  /**
   * 获取星期几的中文显示
   */
  static getWeekdayName(date: Date | string, serverTime?: string): string {
    let dateObj: Date;
    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/) && serverTime) {
        dateObj = this.serverToLocal(date, serverTime);
      } else if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateObj = this.parseLocalDate(date);
      } else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }
    
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return weekdays[dateObj.getDay()];
  }

  /**
   * 解析服务器返回的时间字符串为本地 Date 对象
   * 
   * 服务器返回的时间可能有以下格式：
   * 1. 带 Z 后缀的 UTC 时间: "2024-12-03T10:30:00.000Z"
   * 2. 带时区偏移的时间: "2024-12-03T10:30:00+08:00"
   * 3. 不带时区信息的时间: "2024-12-03T10:30:00" 或 "2024-12-03T10:30:00.000"
   * 
   * 对于第3种情况，我们假设是 UTC 时间（服务器应该返回 UTC）
   * 
   * @param dateStr 服务器返回的时间字符串
   * @returns 本地 Date 对象，可直接用于显示
   */
  static parseServerTime(dateStr: string | Date | null | undefined): Date {
    // 处理空值
    if (!dateStr) {
      return new Date();
    }
    
    // 如果已经是 Date 对象，直接返回
    if (dateStr instanceof Date) {
      return dateStr;
    }
    
    // 如果已经包含时区信息（Z 或 +/-），直接解析
    if (dateStr.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
      return new Date(dateStr);
    }
    
    // 否则假设是 UTC 时间，添加 Z 后缀
    // 这样 JavaScript 会将其视为 UTC 并正确转换到本地时区
    return new Date(dateStr + 'Z');
  }

  /**
   * 格式化聊天时间（自动使用设备本地时区）
   * @param dateStr 服务器返回的时间字符串
   * @returns 格式化后的时间字符串
   */
  static formatChatTime(dateStr: string | Date | null | undefined): string {
    const date = this.parseServerTime(dateStr);
    const now = new Date();
    
    // 比较日期部分（使用本地日期）
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.floor((nowOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // 今天：显示时间 HH:mm
      return date.toLocaleTimeString(undefined, { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } else if (diffDays === 1) {
      return '昨天';
    } else if (diffDays < 7) {
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
      return weekdays[date.getDay()];
    } else {
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }
  }

  /**
   * 格式化消息气泡下方的时间（显示完整时间）
   * @param dateStr 服务器返回的时间字符串
   * @returns HH:mm 格式的时间字符串
   */
  static formatMessageTime(dateStr: string | Date | null | undefined): string {
    const date = this.parseServerTime(dateStr);
    return date.toLocaleTimeString(undefined, { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  }

  /**
   * 格式化日期分隔符
   * @param dateStr 服务器返回的时间字符串
   * @returns 格式化后的日期字符串
   */
  static formatDateSeparator(dateStr: string | Date | null | undefined): string {
    const date = this.parseServerTime(dateStr);
    const now = new Date();
    
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.floor((nowOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return '今天';
    } else if (diffDays === 1) {
      return '昨天';
    } else if (diffDays < 7) {
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
      return weekdays[date.getDay()];
    } else if (date.getFullYear() === now.getFullYear()) {
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    } else {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    }
  }

  /**
   * 判断两个时间是否需要显示日期分隔符
   * @param prevDateStr 前一条消息的时间
   * @param currentDateStr 当前消息的时间
   * @returns 是否需要显示日期分隔符
   */
  static shouldShowDateSeparator(
    prevDateStr: string | Date | null | undefined, 
    currentDateStr: string | Date | null | undefined
  ): boolean {
    if (!prevDateStr) return true;
    
    const prevDate = this.parseServerTime(prevDateStr);
    const currentDate = this.parseServerTime(currentDateStr);
    
    // 比较日期部分
    return prevDate.getFullYear() !== currentDate.getFullYear() ||
           prevDate.getMonth() !== currentDate.getMonth() ||
           prevDate.getDate() !== currentDate.getDate();
  }
}